// Angular
import { Component, OnInit, ElementRef, ViewChild, ChangeDetectionStrategy, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
// Material
import { MatPaginator, MatSort, MatDialog } from '@angular/material';
import { SelectionModel } from '@angular/cdk/collections';
// RXJS
import { debounceTime, distinctUntilChanged, tap, skip, delay } from 'rxjs/operators';
import { fromEvent, merge, Observable, of, Subscription } from 'rxjs';
// NGRX
import { Store, select } from '@ngrx/store';
import { AppState } from '../../../../../core/reducers';
// UI
import { SubheaderService } from '../../../../../core/_base/layout';
// CRUD
import { LayoutUtilsService, MessageType, QueryParamsModel } from '../../../../../core/_base/crud';
// Services and Models
import { 
  selectShiftsPageLastQuery,
  Shift, 
  ShiftDelete, 
  ShiftEdit, 
  ShiftGetPage, 
  ShiftsDataSource 
} from '../../../../../core/plant-configuration';
import { selectAuthState } from '../../../../../core/auth/_selectors/auth.selectors';
import { User } from '../../../../../core/auth';
import { CompanyService } from '../../../../../core/auth/_services/company.service';

@Component({
  selector: 'kt-shifts-list',
  templateUrl: './shifts-list.component.html',
  styleUrls: ['./shifts-list.component.scss']
})
export class ShiftsListComponent implements OnInit, OnDestroy {

  // Table fields
  dataSource: ShiftsDataSource;
  displayedColumns = ['select', 'id', 'type', 'name', 'status', 'horario', 
    'collation_duration', 'shift_duration', 'actions'];
  @ViewChild(MatPaginator, {static: true}) paginator: MatPaginator;
  @ViewChild('sort1', {static: true}) sort: MatSort;
  // Filter fields
  @ViewChild('searchInput', {static: true}) searchInput: ElementRef;

  lastQuery: QueryParamsModel;
	user: User = new User();

  // Selection
  selection = new SelectionModel<Shift>(true, []);
  shiftsResult: Shift[] = [];
  filterActive: string = "";
  private subscriptions: Subscription[] = [];

  /**
   * Component constructor
   *
   * @param dialog: MatDialog
   * @param activatedRoute: ActivatedRoute
   * @param router: Router
   * @param subheaderService: SubheaderService
   * @param layoutUtilsService: LayoutUtilsService
   * @param store: Store<AppState>
   */
  constructor(public dialog: MatDialog,
              private activatedRoute: ActivatedRoute,
              private router: Router,
              private subheaderService: SubheaderService,
              private layoutUtilsService: LayoutUtilsService,
              private store: Store<AppState>,
              private companyService:CompanyService
             ) { }

getCurrentCompany(){
  const currentCompany = this.companyService.getCurrentCompany();
    if(currentCompany == null){
      return "Compañia sin definir"
    }
    return currentCompany.name;
}

  /**
   * @ Lifecycle sequences => https://angular.io/guide/lifecycle-hooks
   */

  /**
   * On init
   */
  ngOnInit() {
    this.user.clear();
    const authSubscription = this.store.pipe(
			select(selectAuthState),
		).subscribe((auth) => {
			// console.log("auth state: "+JSON.stringify(auth));
			if(auth.isUserLoaded)
				this.user = auth.user;
			else
				console.log("user list component: usuario no logueado");
		});
    this.subscriptions.push(authSubscription);
    
    // If the user changes the sort order, reset back to the first page.
    const sortSubscription = this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));
    this.subscriptions.push(sortSubscription);

    /* Data load will be triggered in two cases:
    - when a pagination event occurs => this.paginator.page
    - when a sort event occurs => this.sort.sortChange
    **/
    const paginatorSubscriptions = merge(this.sort.sortChange, this.paginator.page).pipe(
      tap(() => this.loadShiftsList())
    )
    .subscribe();
    this.subscriptions.push(paginatorSubscriptions);

    // Filtration, bind to searchInput
    const searchSubscription = fromEvent(this.searchInput.nativeElement, 'keyup').pipe(
      debounceTime(150),
      distinctUntilChanged(),
      tap(() => {
        this.paginator.pageIndex = 0;
        this.loadShiftsList();
      })
    )
    .subscribe();
    this.subscriptions.push(searchSubscription);

    // // Set title to page breadCrumbs
    // this.subheaderService.setTitle('Turnos');
		// this.subheaderService.setBreadcrumbs([
		// 	{ title: 'Listado de turnos', page: `/plant-configuration/shifts` },
		// 	// { title: 'part 2', page: `/ecommerce/products` },
		// 	// { title: 'part 3', page: `/ecommerce/products/edit`, queryParams: { id: 1 } }
		// ]);
    // // this.subheaderService.updateBreadcrumbs();
    // this.subheaderService.disabled$.next(false);

    // Init DataSource
    this.dataSource = new ShiftsDataSource(this.store);
    const entitiesSubscription = this.dataSource.entitySubject.pipe(
    ).subscribe(res => {
      console.log("shift result: ", res)
      this.shiftsResult = res.map(s=>{
        const startDay = this.getStartDay(s);
        const endDay = this.getEndDay(s);
        return {...s, start_time_day_label: startDay, end_time_day_label: endDay}
      });
    });
    this.subscriptions.push(entitiesSubscription);
    const lastQuerySubscription = this.store.pipe(select(selectShiftsPageLastQuery)).subscribe(res => this.lastQuery = res);
    // Load last query from store
    this.subscriptions.push(lastQuerySubscription);

    // Read from URL itemId, for restore previous state
    const routeSubscription = this.activatedRoute.queryParams.subscribe(params => {
      if (params.id) {
        this.restoreState(this.lastQuery, +params.id);
      }

      // First load
      of(undefined).pipe(delay(1000)).subscribe(() => { // Remove this line, just loading imitation
        this.loadShiftsList();
      }); // Remove this line, just loading imitation
    });
    this.subscriptions.push(routeSubscription);
    this.subscriptions.push(entitiesSubscription);
  }

  getStartDay(shift){
    const days = ['Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab', 'Dom'];
    const startDay = days[shift.start_time_day-1];
    return startDay
  }

  getEndDay(shift){
    const days = ['Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab', 'Dom'];
    const endDay = days[shift.end_time_day-1];
    return endDay
  }

  /**
   * On Destroy
   */
  ngOnDestroy() {
    this.subscriptions.forEach(el => el.unsubscribe());
  }

  /**
   * Load Shifts List
   */
  loadShiftsList() {
    this.selection.clear();
    const queryParams = new QueryParamsModel(
      this.filterConfiguration(),
      this.sort.direction,
      this.sort.active,
      this.paginator.pageIndex,
      this.paginator.pageSize
    );
    // Call request from server
    console.log("loadShiftList");
    this.store.dispatch(new ShiftGetPage({ page: queryParams }));
    this.selection.clear();
  }

  /**
   * Returns object for filter
   */
  filterConfiguration(): any {
    const filter: any = {};
    const searchText: string = this.searchInput.nativeElement.value;

    if(searchText && searchText.length > 0){
      filter.shifts = searchText;
    }
    if(Number(this.filterActive) > 0){
      filter.shifts_status_id = Number(this.filterActive)
    }
    console.log("filter configuration: "+JSON.stringify(filter));
    console.log("filter active: ", this.filterActive)
    return filter;
  }

  /**
   * Restore state
   *
   * @param queryParams: QueryParamsModel
   * @param id: number
   */
  restoreState(queryParams: QueryParamsModel, id: number) {

    if (!queryParams.filter) {
      return;
    }

    if (queryParams.filter.model) {
      this.searchInput.nativeElement.value = queryParams.filter.model;
    }
  }

  /** ACTIONS */
  /**
   * Delete shift
   *
   * @param _item: Shift
   */
  deleteShift(_item: Shift) {
    const _title = 'Eliminar turno';
    const _description = 'Está usted seguro de que desea eliminar permanentemente este turno?';
    const _waitDesciption = 'El turno se está eliminando...';
    const _deleteMessage = `El turno ha sido eliminada`;

    const dialogRef = this.layoutUtilsService.deleteElement(_title, _description, _waitDesciption);
    dialogRef.afterClosed().subscribe(res => {
      if (!res) {
        return;
      }

      this.store.dispatch(new ShiftDelete({shift:{ id: _item.id }}));
      this.loadShiftsList();
      this.layoutUtilsService.showActionNotification(_deleteMessage, MessageType.Delete);
    });
  }

  /**
   * Delete shifts
   */
  deleteShifts() {
    // const _title = 'Products Delete';
    // const _description = 'Are you sure to permanently delete selected products?';
    // const _waitDesciption = 'Products are deleting...';
    // const _deleteMessage = 'Selected products have been deleted';

    // const dialogRef = this.layoutUtilsService.deleteElement(_title, _description, _waitDesciption);
    // dialogRef.afterClosed().subscribe(res => {
    // 	if (!res) {
    // 		return;
    // 	}

    // 	const idsForDeletion: number[] = [];
    // 	// tslint:disable-next-line:prefer-for-of
    // 	for (let i = 0; i < this.selection.selected.length; i++) {
    // 		idsForDeletion.push(this.selection.selected[i].id);
    // 	}
    // 	this.store.dispatch(new ManyProductsDeleted({ ids: idsForDeletion }));
    // 	this.layoutUtilsService.showActionNotification(_deleteMessage, MessageType.Delete);
    // 	this.selection.clear();
    // });
  }

  /**
   * Fetch selected shifts
   */
  fetchShifts() {
    // tslint:disable-next-line:prefer-const
    let messages = [];
    this.selection.selected.forEach(elem => {
      messages.push({
        text: `${elem.name} ${elem.start_time_day_label} ${elem.start_time_hour}:${elem.start_time_minute} - ${elem.end_time_day_label} ${elem.end_time_hour}:${elem.end_time_minute}`,
        id: elem.id,
      });
    });
    this.layoutUtilsService.fetchElements(messages);
  }

  /**
   * Update status dialog
   */
//   updateStatusForShifts() {
//     const _title = 'Update status for selected shifts';
//     const _updateMessage = 'Status has been updated for selected shifts';
//     const _statuses = [{ value: 0, text: 'Selling' }, { value: 1, text: 'Sold' }];
//     const _messages = [];

//     this.selection.selected.forEach(elem => {
//       _messages.push({
//         text: `${elem.name} ${elem.iiot_code}`,
//         id: elem.id,
//       });
//     });

//     const dialogRef = this.layoutUtilsService.updateStatusForEntities(_title, _statuses, _messages);
//     dialogRef.afterClosed().subscribe(res => {
//       if (!res) {
//         this.selection.clear();
//         return;
//       }

//       this.store.dispatch(new ShiftEdit({
//         shift: this.selection.selected
//       }));

//       this.layoutUtilsService.showActionNotification(_updateMessage, MessageType.Update);
//       this.selection.clear();
//     });
//   }

  /**
   * Redirect to edit page
   *
   * @param id: any
   */
  editShift(id) {
    this.router.navigate(['../shifts/edit', id], { relativeTo: this.activatedRoute });
  }

  createShift() {
    this.router.navigateByUrl('/plant-configuration/shifts/add');
  }

  /**
   * Check all rows are selected
   */
  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.shiftsResult.length;
    return numSelected === numRows;
  }

  /**
   * Selects all rows if they are not all selected; otherwise clear selection
   */
  masterToggle() {
    if (this.isAllSelected()) {
      this.selection.clear();
    } else {
      this.shiftsResult.forEach(row => this.selection.select(row));
    }
  }

  /* UI */
  /**
   * Returns status string
   *
   * @param status: number
   */
  getItemStatusString(status: number = 0): string {
    switch (status) {
      case 0:
        return 'Selling';
      case 1:
        return 'Sold';
    }
    return '';
  }

  /**
   * Returns CSS Class by status
   *
   * @param status: number
   */
  getItemCssClassByStatus(status: number = 0): string {
    switch (status) {
      case 0:
        return 'success';
      case 1:
        return 'metal';
    }
    return '';
  }

  /**
   * Rerurns condition string
   *
   * @param condition: number
   */
  getItemConditionString(condition: number = 0): string {
    switch (condition) {
      case 0:
        return 'New';
      case 1:
        return 'Used';
    }
    return '';
  }
  turno
  /**
   * Returns CSS Class by condition
   *
   * @param condition: number
   */
  getItemCssClassByCondition(condition: number = 0): string {
    switch (condition) {
      case 0:
        return 'accent';
      case 1:
        return 'primary';
    }
    return '';
  }

}